/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  Attention,
  Loading,
  RouteDefinition,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { Transactions } from "../components/Transactions/index.js";
import {
  revalidatePublicAccounts,
  usePublicAccounts,
} from "../hooks/account.js";

/**
 * Show histories of public accounts.
 */
export function PublicHistoriesPage({
  routeOverview,
}: {
  routeOverview?: RouteDefinition;
}): VNode {
  const { i18n } = useTranslationContext();

  // TODO: implemented filter by account name
  const result = usePublicAccounts(undefined);
  const [showAccount, setShowAccount] = useState<string>();

  if (!result) {
    return <Loading />;
  }
  if (result.tag === "error") {
    return (
      <Attention
        type="danger"
        title={i18n.str`Failed to load public accounts.`}
      >
        <button
          type="button"
          class="font-semibold underline"
          onClick={() => void revalidatePublicAccounts()}
        >
          <i18n.Translate>Try again</i18n.Translate>
        </button>
      </Attention>
    );
  }

  const accountList = result.value.items;
  const selectedAccount = accountList.some(
    ({ username }) => username === showAccount,
  )
    ? showAccount
    : accountList[0]?.username;

  return (
    <section>
      {routeOverview ? (
        <a
          class="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-primaryDark hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          href={routeOverview.url({})}
        >
          <svg
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          <i18n.Translate>Back to overview</i18n.Translate>
        </a>
      ) : undefined}
      <header>
        <h1 class="text-2xl font-bold tracking-tight text-onBackground">
          <i18n.Translate>Public accounts</i18n.Translate>
        </h1>
        <p class="mt-2 text-sm text-secondary">
          <i18n.Translate>
            Browse transaction histories shared by public bank accounts.
          </i18n.Translate>
        </p>
      </header>

      {selectedAccount === undefined ? (
        <div class="mt-6 rounded-lg border border-outlineVariant bg-primaryMuted p-6 text-sm text-secondary">
          <i18n.Translate>No public accounts are available.</i18n.Translate>
        </div>
      ) : (
        <Fragment>
          <nav aria-label={i18n.str`Public accounts`} class="mt-6">
            <ul class="flex flex-wrap gap-2">
              {accountList.map((account) => {
                const isSelected = account.username === selectedAccount;
                return (
                  <li key={account.username}>
                    <button
                      type="button"
                      name={`show account ${account.username}`}
                      aria-pressed={isSelected}
                      data-selected={isSelected}
                      class="rounded-full border border-outlineVariant bg-white px-4 py-2 text-sm font-semibold text-secondary hover:border-primary/40 hover:text-primaryDark data-[selected=true]:border-primary data-[selected=true]:bg-primaryMuted data-[selected=true]:text-primaryDark"
                      onClick={() => setShowAccount(account.username)}
                    >
                      {account.username}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div class="mt-6">
            <Transactions account={selectedAccount} anonymous />
          </div>
        </Fragment>
      )}
    </section>
  );
}
